import PlayCanvasService from "../../../services/PlayCanvasService/PlayCanvasService";
import { deepCopy } from "../../../utils/objUtils";
import { changeShowModal } from "../../reducers/app/appReducer";
import { getObjectProcessData } from "../../reducers/ui/selectors/objectProcessSelectors";
import {
  getPathToRoot,
  getSelectObjectDataById,
  getUpdateSelectDataByIdAndNewData,
  getUpdateSelectDataByNewData,
} from "../../reducers/ui/selectors/objectSelectors";
import {
  getIsSearching,
  getSearchProjectPathsByProjectId,
} from "../../reducers/ui/selectors/searchSelectors";
import {
  removeSelectObjectData,
  replaceSelectObjectData,
  setActiveObjectId,
  setSelectObjectData,
  updateProcessGLBData,
  updateProcessObjectImage,
} from "../../reducers/ui/uiReducer";
import {
  IProduct,
  ISelectItemObjectData,
  optionObjectOptionT,
} from "../../reducers/ui/uiTypes";
import { AppStore } from "../../store";
import { loadGLBByProductId, replaceGLBByProductId } from "./glbHandlers";
import { loadImageProduct } from "./imageProductHandlers";
import { selectObjectById } from "../../../utils/functions/playcanvas/selectObjectById";

export const removeObjectHandler = (productId: string) => (store: AppStore) => {
  store.dispatch(changeShowModal({ nameModal: "loader", stateModal: true }));

  PlayCanvasService.removeAssetById(productId)
    .then(() => {
      store.dispatch(
        removeSelectObjectData({
          id: productId,
        })
      );
    })
    .finally(() => {
      store.dispatch(
        changeShowModal({ nameModal: "loader", stateModal: false })
      );
    });
};

export const finishedProcessObject =
  (params: {
    productId: string;
    prevProductId: string | null;
    finishGlb?: boolean;
    finishImage?: boolean;
    data: Partial<ISelectItemObjectData>;
  }) =>
  (store: AppStore) => {
    const state = store.getState();
    const { productId, finishGlb, finishImage, data, prevProductId } = params;

    let { tempSelectData, imageHash, renderGLBHash } =
      getObjectProcessData(state);

    if (!tempSelectData) return;

    let selectData = getUpdateSelectDataByNewData(tempSelectData, data)(state);

    if (finishImage) {
      store.dispatch(
        updateProcessObjectImage({
          id: renderGLBHash ? productId : null,
          prevProductId: renderGLBHash ? prevProductId : null,
          imageHash: null,
          selectData: renderGLBHash ? selectData : null,
        })
      );
      imageHash = null; // Reset imageHash after processing
    }

    if (finishGlb) {
      store.dispatch(
        updateProcessGLBData({
          id: imageHash ? productId : null,
          prevProductId: imageHash ? prevProductId : null,
          glbHash: null,
          selectData: imageHash ? selectData : null,
        })
      );
      renderGLBHash = null; // Reset renderGLBHash after processing
    }

    if ((!imageHash && !renderGLBHash) || (!renderGLBHash && imageHash)) {
      const state = store.getState();

      const selectDataExist = getSelectObjectDataById(productId)(state);
      if (selectDataExist) {
        selectData = getUpdateSelectDataByNewData(selectDataExist, data)(state);
      }

      if (prevProductId) {
        store.dispatch(
          replaceSelectObjectData({
            prevId: prevProductId,
            id: productId,
            data: selectData,
          })
        );
      } else {
        store.dispatch(
          setSelectObjectData({
            id: productId,
            data: selectData,
          })
        );
      }

      store.dispatch(
        changeShowModal({ nameModal: "loader", stateModal: false })
      );
    }
  };

export const replaceObjectSceneData =
  (
    params: {
      prevProductId: string;
      productId: string;
    },
    selectData: ISelectItemObjectData
  ) =>
  (store: AppStore) => {
    const state = store.getState();
    store.dispatch(changeShowModal({ nameModal: "loader", stateModal: true }));

    const arrPromises: Promise<Partial<ISelectItemObjectData> | null>[] = [
      replaceGLBByProductId(
        params.prevProductId,
        params.productId,
        selectData
      )(store),
      loadImageProduct(
        params.productId,
        selectData,
        params.prevProductId
      )(store),
    ];

    Promise.all(arrPromises)
      .then(data => {
        let resSelectData: ISelectItemObjectData = deepCopy(selectData);
        // const isEverSuccess = data.every((item) => item);

        const isGLBDone = data[0];

        if (isGLBDone) {
          data.forEach(updSelectData => {
            if (updSelectData) {
              resSelectData = getUpdateSelectDataByNewData(
                resSelectData,
                updSelectData
              )(state);
            }
          });

          store.dispatch(
            replaceSelectObjectData({
              prevId: params.prevProductId,
              id: params.productId,
              data: resSelectData,
            })
          );

          // Update the new active product.
          store.dispatch(setActiveObjectId(params.productId));

          store.dispatch(
            changeShowModal({ nameModal: "loader", stateModal: false })
          );

          // After replacing with a new uiId, ensure selection outline is restored
          setTimeout(() => {
            selectObjectById(params.productId);
          }, 150);
        }
      })
      .catch(error => {
        console.error("Error loading object scene data:", error);
      });
  };

export const loadObjectSceneData =
  (
    params: {
      productId: string;
      replaceGLB?: boolean;
      loadGLB?: boolean;
      /** When true, re-select in PlayCanvas to restore outline */
      restoreSelection?: boolean;
      /** Suppress global full-screen loader modal */
      showGlobalLoader?: boolean;
    },
    selectData: ISelectItemObjectData
  ) =>
  (store: AppStore) => {
    const state = store.getState();
    const showLoader = params.showGlobalLoader !== false;
    if (showLoader) {
      store.dispatch(
        changeShowModal({ nameModal: "loader", stateModal: true })
      );
    }

    const arrPromises: Promise<Partial<ISelectItemObjectData> | null>[] = [];

    if (params.loadGLB) {
      arrPromises.push(
        loadGLBByProductId(
          params.productId,
          selectData,
          params.replaceGLB
        )(store)
      );
    }

    arrPromises.push(loadImageProduct(params.productId, selectData)(store));

    Promise.allSettled(arrPromises)
      .then(results => {
        let resSelectData: ISelectItemObjectData = deepCopy(selectData);

        results.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value) {
            resSelectData = getUpdateSelectDataByNewData(
              resSelectData,
              result.value
            )(state);
          } else if (result.status === 'rejected') {
            console.error(`Promise ${index} failed:`, result.reason);
          }
        });

        store.dispatch(
          setSelectObjectData({
            id: params.productId,
            data: resSelectData,
          })
        );

        if (showLoader) {
          store.dispatch(
            changeShowModal({ nameModal: "loader", stateModal: false })
          );
        }

        // If requested, re-apply selection to bring back the outline
        if (params.restoreSelection) {
          setTimeout(() => {
            selectObjectById(params.productId);
          }, 150);
        }
      });
  };

export const updateValueOption =
  (
    productId: string,
    type: optionObjectOptionT,
    updSelectData: Partial<ISelectItemObjectData>
  ) =>
  (store: AppStore) => {
    const state = store.getState();

    const selectData = getUpdateSelectDataByIdAndNewData(
      productId,
      updSelectData
    )(state);

    // Optimistically update UI selection so active swatch reflects immediately
    store.dispatch(
      setSelectObjectData({
        id: productId,
        data: selectData,
      })
    );

    loadObjectSceneData(
      {
        productId: productId,
        loadGLB: type === "geometry" ? true : false,
        replaceGLB: true,
        restoreSelection: true,
        // Suppress global loader for quick ListItems variant changes
        showGlobalLoader: false,
      },
      selectData
    )(store);
  };

export const initSelectObjectData =
  (projectId: number, product: IProduct) => (store: AppStore) => {
    const state = store.getState();
    const pathObject = getPathToRoot(state);
    let pathObjectIds = pathObject.map(obj => obj.id);

    const isSearching = getIsSearching(state);
    if (isSearching) {
      pathObjectIds = getSearchProjectPathsByProjectId(projectId)(state);
    }

    if (!PlayCanvasService.initHandlers) {
      initHandlersPlaycanvas()(store);
      PlayCanvasService.initHandlers = true;
    }

    const { materialOptions, geometryOptions } =
      getInitObjectSelectOptionsByProduct(product);

    const selectData: ISelectItemObjectData = {
      projectId: projectId,
      realId: product.id,
      pathObjectIds,
      materialOptions: materialOptions,
      geometryOptions: geometryOptions,
    };

    const uiId = getUniqueIdForSelectObject(
      product.id,
      projectId,
      pathObjectIds
    );

    loadObjectSceneData(
      {
        productId: uiId,
        loadGLB: true,
      },
      selectData
    )(store);

    store.dispatch(setActiveObjectId(uiId));
  };

export const replaceOldToNewSelectObjectData =
  (projectId: number, product: IProduct, prevProductId: string) =>
  (store: AppStore) => {
    const state = store.getState();
    const pathObject = getPathToRoot(state);
    let pathObjectIds = pathObject.map(obj => obj.id);

    const isSearching = getIsSearching(state);
    if (isSearching) {
      pathObjectIds = getSearchProjectPathsByProjectId(projectId)(state);
    }

    const { materialOptions, geometryOptions } =
      getInitObjectSelectOptionsByProduct(product);

    const selectData: ISelectItemObjectData = {
      projectId: projectId,
      realId: product.id,
      pathObjectIds,
      materialOptions: materialOptions,
      geometryOptions: geometryOptions,
    };

    const uiId = getUniqueIdForSelectObject(
      product.id,
      projectId,
      pathObjectIds
    );

    replaceObjectSceneData(
      {
        prevProductId: prevProductId,
        productId: uiId,
      },
      selectData
    )(store);

    store.dispatch(setActiveObjectId(uiId));
  };

function getInitObjectSelectOptionsByProduct(product: IProduct) {
  return {
    materialOptions: getInitObjectSelectOptionDataByOptions(
      product.availableOptions
    ),
    geometryOptions: getInitObjectSelectOptionDataByOptions(
      product.availableGeometryOptions
    ),
  };
}

function getInitObjectSelectOptionDataByOptions(
  options: {
    id: number;
    options: { id: number; variants: { id: number; enabled?: boolean }[] }[];
  }[]
) {
  return options.reduce<Record<number, any>>((acc, proxy) => {
    const firstOptionWithVariant = proxy.options.find(
      opt => Array.isArray(opt.variants) && opt.variants.length > 0
    );

    if (firstOptionWithVariant) {
      const enabledVariant = firstOptionWithVariant.variants.find(
        v => v.enabled !== false
      );
      const selectedVariant =
        enabledVariant || firstOptionWithVariant.variants[0];

      acc[proxy.id] = {
        [firstOptionWithVariant.id]: selectedVariant.id,
      };
    } else {
      acc[proxy.id] = {};
    }
    return acc;
  }, {});
}

function initHandlersPlaycanvas() {
  return (store: AppStore) => {
    PlayCanvasService.addListenerMouseUp(async () => {
      const selectObject = PlayCanvasService.getSelectedObject();
      if (!selectObject) return;

      const state = store.getState();

      const selectData = getSelectObjectDataById(selectObject.id as string)(
        state
      );

      if (!selectData || !selectData.glbData) return;

      const transformData = await PlayCanvasService.getTransformDataAssetById(
        selectObject.id
      );

      const isCompared = PlayCanvasService.comparedTransformAssetById(
        transformData,
        selectData.glbData?.transform
      );

      if (!isCompared) {
        const updSelectData = getUpdateSelectDataByNewData(selectData, {
          glbData: {
            url: selectData.glbData?.url,
            transform: transformData,
          },
        })(state);

        store.dispatch(
          setSelectObjectData({
            id: selectObject.id as string,
            data: updSelectData,
          })
        );
      }
    });

    PlayCanvasService.onSelectedSimple(data => {
      // Guard against null/undefined selection from engine.
      if (!data || data.id == null) {
        store.dispatch(setActiveObjectId(null));
        return;
      }

      const state = store.getState();
      // Try direct UI id match first
      const direct = getSelectObjectDataById(String(data.id))(state);
      if (direct) {
        store.dispatch(setActiveObjectId(String(data.id)));
        return;
      }

      // Fallback: map engine id (realId) to our UI id from selectObjects
      const entries = Object.entries(state.ui.selectObjects || {});
      const match = entries.find(
        ([uiId, val]) => (val as any)?.realId === Number(data.id)
      );
      if (match) {
        const [uiId] = match;
        store.dispatch(setActiveObjectId(uiId));
        return;
      }

      store.dispatch(setActiveObjectId(null));
    });
  };
}

function getUniqueIdForSelectObject(
  productId: number,
  projectId: number,
  pathObjectIds: number[]
): string {
  return (
    productId +
    "_" +
    projectId +
    "_" +
    pathObjectIds.join("#") +
    "_" +
    Math.random().toString(36).substring(2, 15)
  );
}
